import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Card, CardActionArea, Typography, Chip, alpha } from '@mui/material';
import { LocalGasStationOutlined, TollOutlined, ChevronRight } from '@mui/icons-material';
import GuidedTour from '../../components/tour/GuidedTour';
import TourButton from '../../components/tour/TourButton';
import useTour from '../../components/tour/useTour';
import { TOURS } from '../../components/tour/tours';

// Utility tools. Add new tools here; each one gets its own card on this page.
const UTILITIES = [
  {
    title: 'Fuel Efficiency',
    description: 'Track vehicle mileage, fuel spend and cost per kilometre.',
    path: '/utilities/fuel-efficiency',
    icon: <LocalGasStationOutlined />,
    color: '#0891b2',
  },
  {
    title: 'FASTag',
    description: 'Keep tabs on FASTag balances, recharges and toll charges.',
    path: '/utilities/fastag',
    icon: <TollOutlined />,
    color: '#7c3aed',
  },
];

const Utilities = () => {
  const navigate = useNavigate();
  const utilitiesTour = useTour('utilities');

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Utilities
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Handy tools for everyday money decisions
          </Typography>
        </Box>
        <TourButton onClick={utilitiesTour.start} />
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, gap: 2 }}>
        {UTILITIES.map((utility) => (
          <Card key={utility.path} data-tour={`utilities-${utility.path.split('/').pop()}`} sx={{ borderRadius: 3, '&:hover': { boxShadow: 'var(--fx-card-shadow-hover)' } }}>
            <CardActionArea onClick={() => navigate(utility.path)} sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'flex-start' }}>
              <Box
                sx={{
                  width: 52,
                  height: 52,
                  borderRadius: 2.5,
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: utility.color,
                  bgcolor: alpha(utility.color, 0.1),
                  '& svg': { fontSize: 28 },
                }}
              >
                {utility.icon}
              </Box>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>{utility.title}</Typography>
                <Typography variant="body2" color="text.secondary">{utility.description}</Typography>
              </Box>
              <ChevronRight sx={{ color: 'text.secondary' }} />
            </CardActionArea>
          </Card>
        ))}
        <Card sx={{ borderRadius: 3, border: '1px dashed', borderColor: 'divider', boxShadow: 'none', bgcolor: 'transparent' }}>
          <Box sx={{ p: 2.5, height: '100%', display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Chip label="Coming soon" size="small" />
            <Typography variant="body2" color="text.secondary">More utilities are on the way.</Typography>
          </Box>
        </Card>
      </Box>
      <GuidedTour steps={TOURS.utilities} open={utilitiesTour.open} onClose={utilitiesTour.close} />
    </Box>
  );
};

export default Utilities;
